import axios from 'axios';
import { Field, Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Card, CardBody, CardHeader, Col, FormGroup, Input, Row } from 'reactstrap';
import * as Yup from 'yup';
import routes from '../../configs/routes';
import {
  ComplaintAPI,
  ComplaintCategory,
  ComplaintFiledBy,
  ComplaintOrderStatus,
  ComplaintStatus,
  ComplaintSubCategory,
} from './complaint-api.type';
import {
  getComplaintFiledByDisplay,
  getComplaintStatusDisplay,
  getReasonSubcategoryDisplay,
} from './complaint.utils';

const validationSchema = Yup.object().shape({
  status: Yup.string().required('Status is required'),
  dueDate: Yup.date().required('Due date is required'),
  complaintDate: Yup.date().required('Complaint date is required'),
  complaintFiledBy: Yup.string().required('Filed by is required'),
  reasonCategory: Yup.string().required('Reason category is required'),
  reasonSubcategory: Yup.string().required('Reason subcategory is required'),
  reasonOtherText: Yup.string().nullable(),
  logisticsLastSeenLocation: Yup.string().nullable(),
  logisticsLastSeenDate: Yup.date().nullable(),
  complaintInfo: Yup.string().required('Complaint information is required'),
  isClosed: Yup.boolean(),
});

const formatDate = (date: string | Date | undefined): string => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString();
};

const formatCloseOrderStatus = (status: ComplaintOrderStatus): string => {
  switch (status) {
    case 'open':
      return 'Open';
    case 'closed':
      return 'Closed';
    case 'not_set':
      return 'Not Set';
    default:
      return status;
  }
};

// Mapping of categories to their valid subcategories
const categoryToSubcategories: Record<ComplaintCategory, ComplaintSubCategory[]> = {
  [ComplaintCategory.BUYER]: [
    ComplaintSubCategory.BUYERS_BEHAVIOR,
    ComplaintSubCategory.WRONG_MATERIALS_BUYER,
    ComplaintSubCategory.WRONG_PROPOSITION,
    ComplaintSubCategory.LACK_IN_SPEECH,
    ComplaintSubCategory.OTHER_BUYER,
  ],
  [ComplaintCategory.PACKAGER]: [
    ComplaintSubCategory.WRONG_MATERIALS_PACKAGER,
    ComplaintSubCategory.LACKING_MATERIALS,
    ComplaintSubCategory.OTHER_PACKAGER,
  ],
  [ComplaintCategory.CUSTOMER_SERVICE]: [
    ComplaintSubCategory.CUSTOMER_SERVICE_BEHAVIOR,
    ComplaintSubCategory.OTHER_CUSTOMER_SERVICE,
  ],
  [ComplaintCategory.VALUATION]: [
    ComplaintSubCategory.WRONG_VALUATION,
    ComplaintSubCategory.OTHER_VALUATION,
  ],
  [ComplaintCategory.TELLER]: [
    ComplaintSubCategory.TELLERS_BEHAVIOR,
    ComplaintSubCategory.WRONG_INFOS,
    ComplaintSubCategory.LOW_PRICE,
    ComplaintSubCategory.OTHER_TELLER,
  ],
  [ComplaintCategory.BURSAR]: [
    ComplaintSubCategory.WRONG_NO_RETURN,
    ComplaintSubCategory.OTHER_BURSAR,
  ],
  [ComplaintCategory.LOGISTICS]: [
    ComplaintSubCategory.PACKAGE_MISSING_INCOMING_MH,
    ComplaintSubCategory.PACKAGE_MISSING_OUTGOING_POSTI,
    ComplaintSubCategory.PACKAGE_MISSING_OUTGOING_MH,
    ComplaintSubCategory.OTHER_LOGISTICS,
  ],
  [ComplaintCategory.OTHER]: [ComplaintSubCategory.OTHER_OTHER],
};

const ComplaintShow: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [complaint, setComplaint] = useState<ComplaintAPI.Complaint | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const handleCloseOrder = async () => {
    if (!complaint?.orderId) {
      setError('No order ID found for this complaint');
      return;
    }

    try {
      const requestData: ComplaintAPI.ComplaintCloseOrderData = {
        complaintId: complaint.id,
      };
      await axios.post(
        `${window.API_ENDPOINT}/ComplaintHandlers/order/${complaint.orderId}/close`,
        requestData
      );
      // Refresh the complaint data
      const response = await axios.get<ComplaintAPI.GetComplaintResponse>(
        `${window.API_ENDPOINT}/ComplaintHandlers/${id}`
      );
      setComplaint(response.data.complaint);
    } catch (err) {
      console.error('Error closing order:', err);
      setError('Failed to close order');
    }
  };

  const handleCloseComplaint = () => {
    // Placeholder function
  };

  useEffect(() => {
    const fetchComplaint = async () => {
      try {
        const response = await axios.get<ComplaintAPI.GetComplaintResponse>(
          `${window.API_ENDPOINT}/ComplaintHandlers/${id}`
        );
        setComplaint(response.data.complaint);
      } catch (err) {
        setError('Failed to load complaint details');
        console.error('Error fetching complaint:', err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchComplaint();
    }
  }, [id]);

  const handleSubmit = async (values: ComplaintAPI.ComplaintUpdateData) => {
    try {
      await axios.put(`${window.API_ENDPOINT}/ComplaintHandlers/${id}`, values);
      // Refresh the complaint data
      const response = await axios.get<ComplaintAPI.GetComplaintResponse>(
        `${window.API_ENDPOINT}/ComplaintHandlers/${id}`
      );
      setComplaint(response.data.complaint);
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating complaint:', err);
      setError('Failed to update complaint');
    }
  };

  const renderOrderButton = (status: ComplaintOrderStatus) => {
    if (status === 'open') {
      return (
        <Button color="danger" className="mr-2" onClick={handleCloseOrder}>
          Close Order
        </Button>
      );
    }
    return null;
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error || !complaint) {
    return <div className="text-danger">{error || 'Complaint not found'}</div>;
  }

  const initialValues: ComplaintAPI.ComplaintUpdateData = {
    status: complaint.status,
    dueDate: new Date(complaint.dueDate).toISOString().split('T')[0],
    complaintDate: new Date(complaint.complaintDate).toISOString().split('T')[0],
    complaintFiledBy: complaint.complaintFiledBy,
    reasonCategory: complaint.reasonCategory,
    reasonSubcategory: complaint.reasonSubcategory,
    reasonOtherText: complaint.reasonOtherText || '',
    logisticsLastSeenLocation: complaint.logisticsLastSeenLocation || '',
    logisticsLastSeenDate: complaint.logisticsLastSeenDate
      ? new Date(complaint.logisticsLastSeenDate).toISOString().split('T')[0]
      : '',
    complaintInfo: complaint.complaintInfo,
    isClosed: complaint.isClosed,
  };

  return (
    <Card>
      <CardHeader className="d-flex justify-content-between align-items-center">
        <h4 className="mb-0">Complaint Details</h4>
        <div>
          {!isEditing && (
            <Button color="primary" className="mr-2" onClick={() => setIsEditing(true)}>
              Edit
            </Button>
          )}
          <Button color="secondary" href={routes.COMPLAINT.INDEX}>
            Back to List
          </Button>
        </div>
      </CardHeader>

      <CardBody>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ errors, touched, values, setFieldValue }) => (
            <Form>
              <Row>
                <Col md={6}>
                  <h5>Basic Information</h5>
                  <dl className="row">
                    <dt className="col-sm-4">Complaint Number</dt>
                    <dd className="col-sm-8">{complaint.complaintNumber}</dd>

                    <dt className="col-sm-4">Status</dt>
                    <dd className="col-sm-8">
                      {isEditing ? (
                        <FormGroup>
                          <Field as={Input} type="select" name="status">
                            {Object.values(ComplaintStatus).map((value) => (
                              <option key={value} value={value}>
                                {getComplaintStatusDisplay(value)}
                              </option>
                            ))}
                          </Field>
                          {errors.status && touched.status && (
                            <div className="text-danger">{errors.status}</div>
                          )}
                        </FormGroup>
                      ) : (
                        getComplaintStatusDisplay(complaint.status)
                      )}
                    </dd>

                    <dt className="col-sm-4">Filed By</dt>
                    <dd className="col-sm-8">
                      {isEditing ? (
                        <FormGroup>
                          <Field as={Input} type="select" name="complaintFiledBy">
                            {Object.values(ComplaintFiledBy).map((value) => (
                              <option key={value} value={value}>
                                {getComplaintFiledByDisplay(value)}
                              </option>
                            ))}
                          </Field>
                          {errors.complaintFiledBy && touched.complaintFiledBy && (
                            <div className="text-danger">{errors.complaintFiledBy}</div>
                          )}
                        </FormGroup>
                      ) : (
                        getComplaintFiledByDisplay(complaint.complaintFiledBy)
                      )}
                    </dd>

                    <dt className="col-sm-4">Created Date</dt>
                    <dd className="col-sm-8">
                      {isEditing ? (
                        <FormGroup>
                          <Field as={Input} type="date" name="complaintDate" />
                          {errors.complaintDate && touched.complaintDate && (
                            <div className="text-danger">{errors.complaintDate}</div>
                          )}
                        </FormGroup>
                      ) : (
                        new Date(complaint.complaintDate).toLocaleDateString()
                      )}
                    </dd>

                    <dt className="col-sm-4">Due Date</dt>
                    <dd className="col-sm-8">
                      {isEditing ? (
                        <FormGroup>
                          <Field as={Input} type="date" name="dueDate" />
                          {errors.dueDate && touched.dueDate && (
                            <div className="text-danger">{errors.dueDate}</div>
                          )}
                        </FormGroup>
                      ) : (
                        new Date(complaint.dueDate).toLocaleDateString()
                      )}
                    </dd>

                    <dt className="col-sm-4">Order Status</dt>
                    <dd className="col-sm-8">
                      {formatCloseOrderStatus(complaint.orderClosed ?? 'not_set')}
                    </dd>
                  </dl>
                </Col>

                <Col md={6}>
                  <h5>Reason Details</h5>
                  <dl className="row">
                    <dt className="col-sm-4">Category</dt>
                    <dd className="col-sm-8">
                      {isEditing ? (
                        <FormGroup>
                          <Field
                            as={Input}
                            type="select"
                            name="reasonCategory"
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const newCategory = e.target.value as ComplaintCategory;
                              setFieldValue('reasonCategory', newCategory);
                              setFieldValue(
                                'reasonSubcategory',
                                categoryToSubcategories[newCategory][0]
                              );
                            }}
                          >
                            {Object.values(ComplaintCategory).map((value) => (
                              <option key={value} value={value}>
                                {value}
                              </option>
                            ))}
                          </Field>
                          {errors.reasonCategory && touched.reasonCategory && (
                            <div className="text-danger">{errors.reasonCategory}</div>
                          )}
                        </FormGroup>
                      ) : (
                        complaint.reasonCategory
                      )}
                    </dd>

                    <dt className="col-sm-4">Subcategory</dt>
                    <dd className="col-sm-8">
                      {isEditing ? (
                        <FormGroup>
                          <Field as={Input} type="select" name="reasonSubcategory">
                            {categoryToSubcategories[
                              values.reasonCategory as ComplaintCategory
                            ].map((value: ComplaintSubCategory) => (
                              <option key={value} value={value}>
                                {getReasonSubcategoryDisplay(value)}
                              </option>
                            ))}
                          </Field>
                          {errors.reasonSubcategory && touched.reasonSubcategory && (
                            <div className="text-danger">{errors.reasonSubcategory}</div>
                          )}
                        </FormGroup>
                      ) : (
                        getReasonSubcategoryDisplay(complaint.reasonSubcategory)
                      )}
                    </dd>

                    {(complaint.reasonOtherText || isEditing) && (
                      <>
                        <dt className="col-sm-4">Other Details</dt>
                        <dd className="col-sm-8">
                          {isEditing ? (
                            <FormGroup>
                              <Field as={Input} type="text" name="reasonOtherText" />
                              {errors.reasonOtherText && touched.reasonOtherText && (
                                <div className="text-danger">{errors.reasonOtherText}</div>
                              )}
                            </FormGroup>
                          ) : (
                            complaint.reasonOtherText
                          )}
                        </dd>
                      </>
                    )}
                  </dl>
                </Col>
              </Row>

              <Row className="mt-4">
                <Col md={12}>
                  <h5>Complaint Information</h5>
                  {isEditing ? (
                    <FormGroup>
                      <Field as={Input} type="textarea" name="complaintInfo" rows={4} />
                      {errors.complaintInfo && touched.complaintInfo && (
                        <div className="text-danger">{errors.complaintInfo}</div>
                      )}
                    </FormGroup>
                  ) : (
                    <p className="whitespace-pre-wrap">{complaint.complaintInfo}</p>
                  )}
                </Col>
              </Row>

              {(complaint.client?.clientNumber || complaint.order?.orderNumber) && (
                <Row className="mt-4">
                  <Col md={6}>
                    <h5>Related Information</h5>
                    <dl className="row">
                      {complaint.client?.clientNumber && (
                        <>
                          <dt className="col-sm-4">Client Number</dt>
                          <dd className="col-sm-8">{complaint.client?.clientNumber}</dd>
                        </>
                      )}
                      {complaint.order?.orderNumber && (
                        <>
                          <dt className="col-sm-4">Order Number</dt>
                          <dd className="col-sm-8">{complaint.order?.orderNumber}</dd>
                        </>
                      )}
                    </dl>
                  </Col>
                </Row>
              )}

              <Row className="mt-4">
                <Col md={6}>
                  <h5>Logistics Information</h5>
                  <dl className="row">
                    <dt className="col-sm-4">Last Seen Location</dt>
                    <dd className="col-sm-8">
                      {isEditing ? (
                        <FormGroup>
                          <Field as={Input} type="text" name="logisticsLastSeenLocation" />
                          {errors.logisticsLastSeenLocation &&
                            touched.logisticsLastSeenLocation && (
                              <div className="text-danger">{errors.logisticsLastSeenLocation}</div>
                            )}
                        </FormGroup>
                      ) : (
                        complaint.logisticsLastSeenLocation || '-'
                      )}
                    </dd>

                    <dt className="col-sm-4">Last Seen Date</dt>
                    <dd className="col-sm-8">
                      {isEditing ? (
                        <FormGroup>
                          <Field as={Input} type="date" name="logisticsLastSeenDate" />
                          {errors.logisticsLastSeenDate && touched.logisticsLastSeenDate && (
                            <div className="text-danger">{errors.logisticsLastSeenDate}</div>
                          )}
                        </FormGroup>
                      ) : (
                        formatDate(complaint.logisticsLastSeenDate)
                      )}
                    </dd>
                  </dl>
                </Col>
              </Row>

              <Row className="mt-4">
                <Col md={12}>
                  {isEditing ? (
                    <div className="d-flex justify-content-end">
                      <Button
                        color="secondary"
                        className="mr-2"
                        onClick={() => setIsEditing(false)}
                      >
                        Cancel
                      </Button>
                      <Button color="primary" type="submit">
                        Save Changes
                      </Button>
                    </div>
                  ) : (
                    <div className="d-flex justify-content-end">
                      {renderOrderButton(complaint.orderClosed || 'not_set')}
                      <Button color="danger" onClick={handleCloseComplaint}>
                        {values.isClosed ? 'Unclose Complaint' : 'Close Complaint'}
                      </Button>
                    </div>
                  )}
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </CardBody>
    </Card>
  );
};

export default ComplaintShow;
